import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "reactstrap";
import moment from "moment/moment";
import { EContentService } from "../../../globals/services/enhancedContent.service";
import { ContentService } from "../../../globals/services/content.service";
import { useSelector } from "react-redux";
import styles from "./template.module.scss"
import { useLocation, useHistory } from "react-router-dom";
import EditHeader from "../components/EditHeader";
import UploadImage from "../../../globals/core/UploadImage";
import { TABS, STATUS } from "../constants";
import FormField from "../components/FormField";
import SelectField from "../components/SelectField";
import FolderView from "./components/FolderView";
import DragTitle from "../components/DragTitle";
import TemplateTabsNavigation from "./components/TemplateTabsNavigation";
import TabModal from "./components/TabModal";
import FolderModal from "./components/FolderModal";
import RemoveFolderModal from "./components/RemoveFolderModal";
import AssignFolderModal from "./components/AssignFolderModal";
import EmptyFoldersMessage from "./components/EmptyFoldersMessage";

const languages = [
  { label: "English", value: "English" },
  { label: "Hebrew", value: "Hebrew" },
  { label: "Arabic", value: "Arabic" },
];

export const Template = () => {
  const history = useHistory();
  const location = useLocation();
  
  // Get template ID from URL path (e.g., /template/123 -> 123)
  const templateId = location.pathname.split('/template/')[1];

  const { me } = useSelector((state) => state.auth);
  const eContentService = EContentService();
  const contentService = ContentService();

  const initialState = {
    _id: templateId,
    title: "",
    status: null,
    version: null,
    volume: null,
    language: null,
    tabItems: [],
    folderItems: [],
    description: null,
    createdBy: me ? me._id : null,
    created: moment().format("YYYY-MM-DD"),
    modifiedBy: me ? me._id : null,
    modified: moment().format("YYYY-MM-DD"),
    thumbnail: null,
  };

  const [state, setState] = useState(initialState);
  const [volumeList, setVolumeList] = useState([]);
  const [fullFolderData, setFullFolderData] = useState([]);
  const [connectedFoldersData, setConnectedFoldersData] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const [tabModalMode, setTabModalMode] = useState('add');
  const [tabToEdit, setTabToEdit] = useState(null);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [allFoldersList, setAllFoldersList] = useState([]);
  const [isRemoveFolderModalOpen, setIsRemoveFolderModalOpen] = useState(false);
  const [folderToRemove, setFolderToRemove] = useState({ id: '', title: '' });
  const [isAssignFolderModalOpen, setIsAssignFolderModalOpen] = useState(false);
  const [folderToAssign, setFolderToAssign] = useState({ id: '', title: '' });

  useEffect(() => {
    me &&
      eContentService.getActiveVolumeList().then((res) => {
        setVolumeList(res.data);
      });
    
    // Load all folders for the folder modal
    if (me) {
      eContentService.getFolderList().then((res) => {
        setAllFoldersList(res.data || []);
      });
    }
  }, [me]);

  // Load template data when component mounts or template ID changes
  useEffect(() => {
    if (templateId && me) {
      loadTemplateData();
    }
  }, [templateId, me]);

  const loadTemplateData = async () => {
    try {
      setIsInitialLoading(true);
      // Get template from the list
      const templateList = await eContentService.getTemplateList();
      const templateData = templateList.data.find(t => t._id === templateId);
      if (templateData) {
        setState(prevState => ({
          ...prevState,
          _id: templateId,
          title: templateData.title || "",
          status: templateData.status || null,
          version: templateData.version || null,
          volume: templateData.volume || null,
          language: templateData.language || null,
          tabItems: templateData.tabItems || [],
          folderItems: templateData.folderItems ? templateData.folderItems.sort((a, b) => (a.order > b.order ? 1 : -1)) : [],
          description: templateData.description || null,
          createdBy: templateData.createdBy || me?._id || null,
          created: templateData.created || moment().format("YYYY-MM-DD"),
          modifiedBy: me?._id || null,
          modified: moment().format("YYYY-MM-DD"),
          thumbnail: templateData.thumbnail || null,
        }));
      }
    } catch (error) {
      console.error('Error loading template data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Set first tab as active by default
  useEffect(() => {
    if (state.tabItems && state.tabItems.length > 0 && !activeTab) {
      const firstTab = state.tabItems.sort((a, b) => a.order - b.order)[0];
      setActiveTab(firstTab);
    }
  }, [state.tabItems, activeTab]);

  // Fetch full folder data for template folder items
  const fetchFullFolderData = async (folderItems) => {
    if (!folderItems || folderItems.length === 0) {
      setFullFolderData([]);
      return;
    }

    try {
      setIsLoadingFolders(true);
      // Get all folder IDs from template folder items
      const folderIds = folderItems.map(item => item._id).filter(id => id);

      if (folderIds.length === 0) {
        setFullFolderData([]);
        return;
      }

      // Fetch all folders and filter by IDs
      const response = await eContentService.getFolderList();
      const allFolders = response.data || [];

      // Get full folder data for template folder items
      const fullData = allFolders.filter(folder => folderIds.includes(folder._id));
      setFullFolderData(fullData);
    } catch (error) {
      console.error('Error fetching full folder data:', error);
      setFullFolderData([]);
    } finally {
      setIsLoadingFolders(false);
    }
  };

  // Fetch full folder data for connected folders (all folders, no permission filtering)
  const fetchConnectedFoldersData = async (folderItems) => {
    if (!folderItems || folderItems.length === 0) {
      setConnectedFoldersData([]);
      return;
    }

    try {
      // Get all folder IDs from template folder items
      const folderIds = folderItems.map(item => item._id).filter(id => id);

      if (folderIds.length === 0) {
        setConnectedFoldersData([]);
        return;
      }

      // Fetch all folders and filter by IDs
      const response = await eContentService.getFolderList();
      const allFolders = response.data || [];

      // Get full folder data for connected folders (no permission filtering)
      const connectedData = allFolders.filter(folder => folderIds.includes(folder._id));
      
      // Sort the connected data by the same order as folderItems
      const sortedConnectedData = folderItems
        .sort((a, b) => a.order - b.order)
        .map(folderItem => {
          const fullData = connectedData.find(f => f._id === folderItem._id);
          return fullData ? { ...fullData, order: folderItem.order } : null;
        })
        .filter(Boolean);
      
      setConnectedFoldersData(sortedConnectedData);
    } catch (error) {
      console.error('Error fetching connected folders data:', error);
      setConnectedFoldersData([]);
    }
  };

  useEffect(() => {
    if (me && state.folderItems) {
      console.log("state.folderItems", state.folderItems);
      fetchFullFolderData(state.folderItems);
      fetchConnectedFoldersData(state.folderItems);
    }
  }, [me, state.folderItems]);

  const handleForm = (prop) => {
    setState({
      ...state,
      ...prop,
    });

    setHasChanges(true);
  };

  const navigateToTemplatesList = () => {
      history.push('/curricula', { curriculaActiveTab: TABS.TEMPLATES });
  };

  const handleSaveTemplate = async () => {
    try {
      setIsLoading(true);
      const saveOperation = state._id ? eContentService.updateTemplate : eContentService.createTemplate;
      await saveOperation(state);
      navigateToTemplatesList();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsLoading(false);
    }
  }

  // Handle remove tab from modal
  const handleRemoveTab = async () => {
    if (tabToEdit) {
      // If template has an ID (exists on server), delete via API
      if (state._id) {
        try {
          const res = await eContentService.deleteTab(state._id, tabToEdit.title);
          if (res.status === 200) {
            await loadTemplateData();
            
            if (activeTab && activeTab.title === tabToEdit.title) {
              setActiveTab(null);
            }
          }
        } catch (error) {
          console.error('Error deleting tab:', error);
        }
      } else {
        // For new templates (no ID yet), just remove from local state
        const updatedTabs = state.tabItems.filter(tab => tab.title !== tabToEdit.title);
        handleForm({ tabItems: updatedTabs });
        
        // If the removed tab was active, clear active tab or set to first available
        if (activeTab && activeTab.title === tabToEdit.title) {
          if (updatedTabs.length > 0) {
            setActiveTab(updatedTabs[0]);
          } else {
            setActiveTab(null);
          }
        }
      }
    }
  };

  const toggleFolderExpansion = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const openContentItemPreview = (e, _id) => {
    e.stopPropagation();
    contentService.getContentItemById(_id).then((content) => {
      if (content[0].type === 'file') {
        window.open(content[0].file, '_blank');
      } else if (content[0].type === 'external_link') {
        window.open(content[0].link, '_blank');
      }
    });
  };

  const onConnectedFoldersDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Get the filtered folders by active tab (these are the ones being displayed and dragged)
    const displayedFolders = getFilteredFoldersByTab();
    
    // Find the folder that was dragged by its ID
    const draggedFolder = displayedFolders.find(folder => folder._id === draggableId);
    if (!draggedFolder) return;
    
    // Remove the dragged folder from the array
    const newDisplayOrder = displayedFolders.filter(folder => folder._id !== draggableId);
    
    // Insert the dragged folder at the new position
    newDisplayOrder.splice(destination.index, 0, draggedFolder);
    
    // Create a map of folder IDs to their new order
    const orderMap = {};
    newDisplayOrder.forEach((folder, index) => {
      orderMap[folder._id] = index + 1;
    });
    
    // Update all folder items with new order
    const updatedFolderItems = state.folderItems.map(item => {
      if (orderMap[item._id] !== undefined) {
        return {
          ...item,
          order: orderMap[item._id]
        };
      }
      return item;
    });
    
    handleForm({ folderItems: updatedFolderItems });
  };

  // Filter folders by active tab
  const getFilteredFoldersByTab = () => {
    if (!activeTab || !connectedFoldersData) {
      return [];
    }

    return connectedFoldersData.filter(folder => {
      if (!folder.customTabs || folder.customTabs.length === 0) {
        return false;
      }

      // Exclude folders with Open Students or Open Faculty permissions
      if (folder.permissions) {
        const permissions = Array.isArray(folder.permissions) 
          ? folder.permissions 
          : [folder.permissions];
        
        if (permissions.some(permission => 
          permission === "OpenStudents" || permission === "OpenFaculty"
        )) {
          return false;
        }
      }

      // Check if this folder has the active tab selected
      return folder.customTabs.some(tab => 
        tab.tabTitle === activeTab.title && tab.tabSelected
      );
    });
  };

  // Get folders that are not assigned to any tab
  const getUnassignedFolders = () => {
    if (!connectedFoldersData) {
      return [];
    }

    return connectedFoldersData.filter(folder => {
      // If folder has no customTabs, it's unassigned
      if (!folder.customTabs || folder.customTabs.length === 0) {
        return true;
      }

      // If folder has customTabs but none are selected for this template, it's unassigned
      return !folder.customTabs.some(tab => 
        tab.templateId === templateId && tab.tabSelected
      );
    });
  };

  // Handle tab selection
  const handleTabSelect = (tab) => {
    setActiveTab(tab);
  };

  // Handle add tab
  const handleAddTab = () => {
    setTabModalMode('add');
    setTabToEdit(null);
    setIsTabModalOpen(true);
  };

  // Handle add folder
  const handleAddFolder = () => {
    setIsFolderModalOpen(true);
  };

  // Handle connect folder to template
  const handleConnectFolder = async (folderId, selectedTabTitle) => {
    try {
      setIsLoading(true);
      
      // Get the folder data
      const folderToUpdate = allFoldersList.find(f => f._id === folderId);
      if (!folderToUpdate) {
        console.error('Folder not found');
        return;
      }

      // Update folder's templates array
      const updatedTemplates = folderToUpdate.templates ? [...folderToUpdate.templates] : [];
      if (!updatedTemplates.includes(templateId)) {
        updatedTemplates.push(templateId);
      }

      // Update folder's customTabs array
      let updatedCustomTabs = folderToUpdate.customTabs ? [...folderToUpdate.customTabs] : [];
      
      // Remove existing tab for this template if exists
      updatedCustomTabs = updatedCustomTabs.filter(tab => !(tab.templateId === templateId && tab.tabTitle === selectedTabTitle));
      
      // Add new tab selection
      updatedCustomTabs.push({
        tabTitle: selectedTabTitle,
        tabOrder: state.tabItems.find(tab => tab.title === selectedTabTitle)?.order || 1,
        templateTitle: state.title,
        templateId: templateId,
        tabSelected: true
      });

      // Update the folder
      const updatedFolder = {
        ...folderToUpdate,
        templates: updatedTemplates,
        customTabs: updatedCustomTabs
      };

      await eContentService.updateFolder(updatedFolder);
      
      // Update local state to show the new connection
      // Update allFoldersList
      setAllFoldersList(prevList => 
        prevList.map(folder => 
          folder._id === folderId ? updatedFolder : folder
        )
      );
      
      // Add the new folder to folderItems
      const newFolderItem = {
        _id: folderId,
        title: updatedFolder.title,
        order: state.folderItems.length + 1
      };
      const updatedFolderItems = [...state.folderItems, newFolderItem];
      handleForm({ folderItems: updatedFolderItems });
      
      // Update connectedFoldersData to include the new folder
      setConnectedFoldersData(prevData => {
        const newFolderData = { ...updatedFolder, order: newFolderItem.order };
        return [...prevData, newFolderData].sort((a, b) => a.order - b.order);
      });
      
      
      // Auto-save only the folderItems change to persist the addition
      try {
        const templateToSave = {
          _id: state._id,
          folderItems: updatedFolderItems
        };
        await eContentService.updateTemplate(templateToSave);
        console.log('Template folderItems auto-saved after folder addition');
      } catch (error) {
        console.error('Error auto-saving template folderItems after folder addition:', error);
      }
      
      setIsFolderModalOpen(false);
    } catch (error) {
      console.error('Error connecting folder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove folder from template
  const handleRemoveFolder = (folderId, folderTitle) => {
    setFolderToRemove({ id: folderId, title: folderTitle });
    setIsRemoveFolderModalOpen(true);
  };

  // Handle assign folder to tab
  const handleAssignFolder = (folderId, folderTitle) => {
    setFolderToAssign({ id: folderId, title: folderTitle });
    setIsAssignFolderModalOpen(true);
  };

  // Handle confirm assign folder to tab
  const handleConfirmAssignFolder = async (selectedTabTitle) => {
    try {
      setIsLoading(true);
      
      // Get the folder data
      const folderToUpdate = allFoldersList.find(f => f._id === folderToAssign.id);
      if (!folderToUpdate) {
        console.error('Folder not found');
        return;
      }

      // Update folder's templates array
      const updatedTemplates = folderToUpdate.templates ? [...folderToUpdate.templates] : [];
      if (!updatedTemplates.includes(templateId)) {
        updatedTemplates.push(templateId);
      }

      // Update folder's customTabs array
      let updatedCustomTabs = folderToUpdate.customTabs ? [...folderToUpdate.customTabs] : [];
      
      // Remove existing tab for this template if exists
      updatedCustomTabs = updatedCustomTabs.filter(tab => !(tab.templateId === templateId && tab.tabTitle === selectedTabTitle));
      
      // Add new tab selection
      updatedCustomTabs.push({
        tabTitle: selectedTabTitle,
        tabOrder: state.tabItems.find(tab => tab.title === selectedTabTitle)?.order || 1,
        templateTitle: state.title,
        templateId: templateId,
        tabSelected: true
      });

      // Update the folder
      const updatedFolder = {
        ...folderToUpdate,
        templates: updatedTemplates,
        customTabs: updatedCustomTabs
      };

      await eContentService.updateFolder(updatedFolder);
      
      // Update local state to show the new connection
      // Update allFoldersList
      setAllFoldersList(prevList => 
        prevList.map(folder => 
          folder._id === folderToAssign.id ? updatedFolder : folder
        )
      );
      
      // Update connectedFoldersData to include the updated folder
      setConnectedFoldersData(prevData => {
        const updatedData = prevData.map(folder => 
          folder._id === folderToAssign.id ? { ...updatedFolder, order: folder.order } : folder
        );
        return updatedData.sort((a, b) => a.order - b.order);
      });
      
      // Also update the folder in the original folder data if it exists
      setFullFolderData(prevData => {
        const updatedData = prevData.map(folder => 
          folder._id === folderToAssign.id ? { ...updatedFolder, order: folder.order } : folder
        );
        return updatedData.sort((a, b) => a.order - b.order);
      });
      
      // Auto-save only the folderItems change to persist the addition
      try {
        const templateToSave = {
          _id: state._id,
          folderItems: state.folderItems
        };
        await eContentService.updateTemplate(templateToSave);
        console.log('Template folderItems auto-saved after folder assignment');
      } catch (error) {
        console.error('Error auto-saving template folderItems after folder assignment:', error);
      }
      
      setIsAssignFolderModalOpen(false);
      setFolderToAssign({ id: '', title: '' });
    } catch (error) {
      console.error('Error assigning folder to tab:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle confirm remove folder
  const handleConfirmRemoveFolder = async () => {
    try {
      setIsLoading(true);
      
      // Get the folder data
      const folderToUpdate = allFoldersList.find(f => f._id === folderToRemove.id);
      if (!folderToUpdate) {
        console.error('Folder not found');
        return;
      }

      // Remove template from folder's templates array
      const updatedTemplates = folderToUpdate.templates ? 
        folderToUpdate.templates.filter(tId => tId !== templateId) : [];

      // Remove all customTabs for this template from folder
      const updatedCustomTabs = folderToUpdate.customTabs ? 
        folderToUpdate.customTabs.filter(tab => tab.templateId !== templateId) : [];

      // Update the folder
      const updatedFolder = {
        ...folderToUpdate,
        templates: updatedTemplates,
        customTabs: updatedCustomTabs
      };

      await eContentService.updateFolder(updatedFolder);
      
      // Update the allFoldersList to reflect the changes
      setAllFoldersList(prevList => 
        prevList.map(folder => 
          folder._id === folderToRemove.id ? updatedFolder : folder
        )
      );
      
      // Update folderItems to remove the deleted folder from template
      const updatedFolderItems = state.folderItems.filter(item => item._id !== folderToRemove.id);
      handleForm({ folderItems: updatedFolderItems });
      
      
      // Auto-save only the folderItems change to persist the deletion
      try {
        const templateToSave = {
          _id: state._id,
          folderItems: updatedFolderItems
        };
        await eContentService.updateTemplate(templateToSave);
        console.log('Template folderItems auto-saved after folder removal');
      } catch (error) {
        console.error('Error auto-saving template folderItems after folder removal:', error);
      }
      
      setIsRemoveFolderModalOpen(false);
      setFolderToRemove({ id: '', title: '' });
    } catch (error) {
      console.error('Error removing folder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit tab
  const handleEditTab = (tab) => {
    setTabModalMode('edit');
    setTabToEdit(tab);
    setIsTabModalOpen(true);
  };

  // Handle save tab (add or edit)
  const handleSaveTab = async (tabData) => {
    if (tabModalMode === 'add') {
      // Add new tab
      const newTab = {
        title: tabData.title,
        permission: tabData.permission,
        order: state.tabItems.length + 1
      };
      
      // If template has an ID (exists on server), create tab via API
      if (state._id) {
        try {
          const res = await eContentService.createTab(state, newTab);
          if (res.status === 201) {
            // Reload template data to get the latest from server
            await loadTemplateData();
            
            // Set the new tab as active after reload
            setTimeout(() => {
              setActiveTab(newTab);
            }, 100);
          }
        } catch (error) {
          console.error('Error creating tab:', error);
        }
      } else {
        // For new templates (no ID yet), just add to local state
        const updatedTabs = [...state.tabItems, newTab];
        handleForm({ tabItems: updatedTabs });
        
        // Set the new tab as active
        setActiveTab(newTab);
      }
    } else {
      // Edit existing tab
      const updatedTabs = state.tabItems.map(tab => 
        tab.title === tabToEdit.title 
          ? { ...tab, title: tabData.title, permission: tabData.permission }
          : tab
      );
      handleForm({ tabItems: updatedTabs });
      
      // Update active tab if it's the one being edited
      if (activeTab && activeTab.title === tabToEdit.title) {
        setActiveTab({ ...activeTab, title: tabData.title, permission: tabData.permission });
      }
    }
  };

  // Handle tab drag and drop
  const onTabsDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // If dropped outside the list
    if (!destination) {
      return;
    }

    // If dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Create new array with reordered tabs
    const newTabItems = Array.from(state.tabItems);
    const [reorderedTab] = newTabItems.splice(source.index, 1);
    newTabItems.splice(destination.index, 0, reorderedTab);

    // Update order property for all tabs
    const updatedTabItems = newTabItems.map((tab, index) => ({
      ...tab,
      order: index + 1
    }));

    handleForm({ tabItems: updatedTabItems });
  };

  // Show initial loading state while template data is being loaded
  if (isInitialLoading || !me) {
    return (
      <div className="content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
              <div className="text-center">
                <div className="loader"></div>
                <div className="loaderText">Loading template data...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {isLoading && (
        <div className={styles.loaderOverlay}>
          <div className="text-center">
            <div className={styles.loader}></div>
            <div className={styles.loaderText}>Saving...</div>
          </div>
        </div>
      )}
      <div className="page-content">
        <div className="container-fluid">

          <Form>
            <div className={styles.section}>
              <EditHeader
                title="Template"
                subTitle="contents"
                createNew={!state._id}
                hasChanges={hasChanges}
                handleGoBack={navigateToTemplatesList}
                handleSave={handleSaveTemplate}
                hasDelete={false}
              // handleDelete={handleDeleteTemplate}
              />

              <UploadImage
                fileSelected={state.thumbnail}
                handleChange={(file) => handleForm({ thumbnail: file })}
              />

              <div className="col-12 col-md-10">
                <FormField
                  type="text"
                  title="Template Name"
                  name="title"
                  value={state.title ? state.title : ""}
                  onChange={(e) => handleForm({ title: e.target.value })}
                  hasMarginTop
                />
                <FormField
                  type="textarea"
                  title="Template Description"
                  name="description"
                  value={state.description ? state.description : ""}
                  onChange={(e) => handleForm({ description: e.target.value })}
                />
              </div>
            </div>

            <div className={styles.section}>
              <Row>
                <Col className={`col-12 col-md-10 ${styles.selectFieldContainer}`}>
                  <SelectField
                    label="Volume"
                    name="volume"
                    options={volumeList.map((volume) => ({ label: volume.title, value: volume._id }))}
                    value={state.volume}
                    onChange={(name, value) => handleForm({ [name]: value })}
                  />
                  <SelectField
                    label="Language"
                    name="language"
                    options={languages}
                    value={state.language}
                    onChange={(name, value) => handleForm({ [name]: value })}
                  />
                  <SelectField
                    label="Status"
                    name="status"
                    options={Object.values(STATUS).map((status) => ({ label: status, value: status }))}
                    value={state.status}
                    onChange={(name, value) => handleForm({ [name]: value })}
                  />
                </Col>

              </Row>
            </div>

            <div className={styles.section}>
              <h5>
                Tabs & Folders
              </h5>
              <DragTitle text="Drag to reorder your tabs and folders" />

              {/* Open Students/Open Faculty Folders Section */}
              {isLoadingFolders ? (
                <Col className={styles.folderSection} md={10}>
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                    <div className="text-center">
                      <div className="loader" style={{ width: '32px', height: '32px' }}></div>
                      <div className="loaderText" style={{ fontSize: '14px', marginTop: '8px' }}>Loading folders...</div>
                    </div>
                  </div>
                </Col>
              ) : (
                (() => {
                  // Filter folders with Open Students or Open Faculty permissions
                  const openPermissionFolders = connectedFoldersData.filter(folder => {
                    if (!folder.permissions) return false;
                    
                    // Handle array case
                    const permissions = Array.isArray(folder.permissions) 
                      ? folder.permissions 
                      : [folder.permissions];
                    
                    return permissions.some(permission => 
                      permission === "OpenStudents" || permission === "OpenFaculty"
                    );
                  });

                  return openPermissionFolders.length > 0 ? (
                    <Col className={styles.folderSection} md={10}>
                      <FolderView
                        folders={openPermissionFolders}
                        expandedFolders={expandedFolders}
                        onToggleExpansion={toggleFolderExpansion}
                        onContentItemPreview={openContentItemPreview}
                        onRemoveFolder={handleRemoveFolder}
                        showDragHandle={false}
                        openMarginLeft={53}
                        showAssignButton={false}
                      />
                    </Col>
                  ) : null;
                })()
              )}

              {/* Unassigned Folders Section */}
              {isLoadingFolders ? (
                <Col className={styles.folderSection} md={10}>
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                    <div className="text-center">
                      <div className="loader" style={{ width: '32px', height: '32px' }}></div>
                      <div className="loaderText" style={{ fontSize: '14px', marginTop: '8px' }}>Loading folders...</div>
                    </div>
                  </div>
                </Col>
              ) : getUnassignedFolders().length > 0 ? (
                <Col className={styles.folderSection} md={10}>
                  <h6 style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                    Folders not assigned to tabs
                  </h6>
                  <FolderView
                    folders={getUnassignedFolders()}
                    expandedFolders={expandedFolders}
                    onToggleExpansion={toggleFolderExpansion}
                    onContentItemPreview={openContentItemPreview}
                    onRemoveFolder={handleRemoveFolder}
                    onAssignFolder={handleAssignFolder}
                    showDragHandle={false}
                    openMarginLeft={53}
                    showAssignButton={state.tabItems && state.tabItems.length > 0}
                  />
                </Col>
              ) : null}

              <Row className="mt-2">
                <Col className="">
                  
                  {/* Template Tabs Navigation */}
                  <TemplateTabsNavigation
                    tabItems={state.tabItems}
                    activeTab={activeTab}
                    onTabSelect={handleTabSelect}
                    onTabsDragEnd={onTabsDragEnd}
                    onEditTab={handleEditTab}
                    onAddTab={handleAddTab}
                    onAddFolder={handleAddFolder}
                  />

                  {isLoadingFolders ? (
                    <Col md={10}>
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                        <div className="text-center">
                          <div className="loader" style={{ width: '32px', height: '32px' }}></div>
                          <div className="loaderText" style={{ fontSize: '14px', marginTop: '8px' }}>Loading folders...</div>
                        </div>
                      </div>
                    </Col>
                  ) : (connectedFoldersData && connectedFoldersData.length > 0) || activeTab ? (
                    getFilteredFoldersByTab().length > 0 ? (
                      <Col md={10}>
                      <FolderView
                        folders={getFilteredFoldersByTab()}
                        expandedFolders={expandedFolders}
                        onToggleExpansion={toggleFolderExpansion}
                        onContentItemPreview={openContentItemPreview}
                        onRemoveFolder={handleRemoveFolder}
                        onDragEnd={onConnectedFoldersDragEnd}
                        showDragHandle={getFilteredFoldersByTab().length > 1}
                        openMarginLeft={getFilteredFoldersByTab().length > 1 ? 53 : 0}
                      />
                      </Col>
                    ) : (
                      <EmptyFoldersMessage 
                        type="noFoldersForTab" 
                        handleAddFolder={handleAddFolder}
                      />
                    )
                  ) : (
                    <EmptyFoldersMessage
                    type="noFolders"
                    handleAddTab={handleAddTab}
                    />
                  )}
                </Col>
              </Row>
            </div>

          </Form>
        </div>
      </div>
      
      {/* Tab Modal */}
      <TabModal
        isOpen={isTabModalOpen}
        toggle={() => setIsTabModalOpen(false)}
        onSave={handleSaveTab}
        onRemove={handleRemoveTab}
        tabToEdit={tabToEdit}
        mode={tabModalMode}
      />

      {/* Folder Modal */}
      <FolderModal
        isOpen={isFolderModalOpen}
        toggle={() => setIsFolderModalOpen(false)}
        onConnectFolder={handleConnectFolder}
        templateTitle={state.title}
        tabItems={state.tabItems}
        foldersList={allFoldersList}
      />

      {/* Remove Folder Modal */}
      <RemoveFolderModal
        isOpen={isRemoveFolderModalOpen}
        toggle={() => setIsRemoveFolderModalOpen(false)}
        onConfirm={handleConfirmRemoveFolder}
        folderTitle={folderToRemove.title}
      />

      {/* Assign Folder Modal */}
      <AssignFolderModal
        isOpen={isAssignFolderModalOpen}
        toggle={() => setIsAssignFolderModalOpen(false)}
        onConfirm={handleConfirmAssignFolder}
        folderTitle={folderToAssign.title}
        tabItems={state.tabItems}
      />
    </div>
  );
};